import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";
import { useV2List, useV2Save, useV2Delete } from "@/hooks/useTransfersV2";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export default function ZonesManager() {
  const { data: zones = [], isLoading } = useV2List("transfer_zones", true);
  const { data: prices = [] } = useV2List("transfer_zone_prices", true);
  const { data: airports = [] } = useV2List("transfer_airports_v2", true);
  const { data: services = [] } = useV2List("transfer_service_types", true);

  const saveZone = useV2Save("transfer_zones");
  const delZone = useV2Delete("transfer_zones");
  const savePrice = useV2Save("transfer_zone_prices");
  const delPrice = useV2Delete("transfer_zone_prices");

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [zoneOpen, setZoneOpen] = useState(false);
  const [zoneEditing, setZoneEditing] = useState<any | null>(null);
  const [zoneForm, setZoneForm] = useState<any>({ code: "", name: "", is_active: true });

  const [priceOpen, setPriceOpen] = useState(false);
  const [priceEditing, setPriceEditing] = useState<any | null>(null);
  const [priceForm, setPriceForm] = useState<any>({
    zone_id: "",
    airport_id: "",
    service_type_id: "",
    base_price: 0,
    max_pax: 1,
    extra_per_pax: 0,
    is_active: true,
  });

  const pricesByZone = prices.reduce((acc: Record<string, any[]>, p: any) => {
    (acc[p.zone_id] ||= []).push(p);
    return acc;
  }, {});

  const openNewZone = () => {
    setZoneEditing(null);
    setZoneForm({ code: "", name: "", is_active: true });
    setZoneOpen(true);
  };
  const openEditZone = (z: any) => {
    setZoneEditing(z);
    setZoneForm({ code: z.code ?? "", name: z.name ?? "", is_active: !!z.is_active });
    setZoneOpen(true);
  };
  const submitZone = async () => {
    const code = (zoneForm.code || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!code || !zoneForm.name) {
      alert("Code and Name are required");
      return;
    }
    await saveZone.mutateAsync({
      id: zoneEditing?.id,
      values: {
        code,
        name: zoneForm.name,
        slug: slugify(zoneForm.name),
        is_active: !!zoneForm.is_active,
      },
    });
    setZoneOpen(false);
  };

  const openNewPrice = (zoneId: string) => {
    setPriceEditing(null);
    setPriceForm({
      zone_id: zoneId,
      airport_id: airports[0]?.id ?? "",
      service_type_id: services[0]?.id ?? "",
      base_price: 0,
      max_pax: 1,
      extra_per_pax: 0,
      is_active: true,
    });
    setPriceOpen(true);
  };
  const openEditPrice = (p: any) => {
    setPriceEditing(p);
    setPriceForm({ ...p });
    setPriceOpen(true);
  };
  const submitPrice = async () => {
    if (!priceForm.zone_id || !priceForm.airport_id || !priceForm.service_type_id) {
      alert("Zone, Airport and Service are required");
      return;
    }
    await savePrice.mutateAsync({
      id: priceEditing?.id,
      values: {
        zone_id: priceForm.zone_id,
        airport_id: priceForm.airport_id,
        service_type_id: priceForm.service_type_id,
        base_price: Number(priceForm.base_price) || 0,
        max_pax: Number(priceForm.max_pax) || 1,
        extra_per_pax: Number(priceForm.extra_per_pax) || 0,
        is_active: !!priceForm.is_active,
      },
    });
    setPriceOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Zones</h2>
        <Button onClick={openNewZone} className="gap-2">
          <Plus className="h-4 w-4" /> Add Zone
        </Button>
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="grid grid-cols-[40px_1fr_1fr_140px_120px] gap-2 px-4 py-3 text-sm font-medium text-muted-foreground bg-muted/50">
          <span></span>
          <span>Code</span>
          <span>Name</span>
          <span>Prices</span>
          <span className="text-right">Actions</span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : zones.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">No zones yet</div>
        ) : (
          zones.map((z: any) => {
            const zPrices = pricesByZone[z.id] || [];
            const isOpen = !!expanded[z.id];
            return (
              <div key={z.id} className="border-t">
                <div className="grid grid-cols-[40px_1fr_1fr_140px_120px] gap-2 px-4 py-3 items-center">
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [z.id]: !e[z.id] }))}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <span className="font-mono font-semibold">{z.code || "-"}</span>
                  <span>{z.name}</span>
                  <Badge variant="secondary" className="w-fit">
                    {zPrices.length} {zPrices.length === 1 ? "price" : "prices"}
                  </Badge>
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditZone(z)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => confirm("Delete this zone and all its prices?") && delZone.mutate(z.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                {isOpen && (
                  <div className="bg-muted/30 px-6 py-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">
                        Prices for {z.code} – {z.name}
                      </p>
                      <Button size="sm" variant="outline" onClick={() => openNewPrice(z.id)} className="gap-2">
                        <Plus className="h-3.5 w-3.5" /> Add Price
                      </Button>
                    </div>

                    {zPrices.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">No prices configured</p>
                    ) : (
                      <div className="rounded-md bg-background border overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="text-muted-foreground">
                            <tr className="border-b">
                              <th className="text-left p-2 px-3 font-medium">Airport</th>
                              <th className="text-left p-2 px-3 font-medium">Service</th>
                              <th className="text-left p-2 px-3 font-medium">Base Price</th>
                              <th className="text-left p-2 px-3 font-medium">Max Pax</th>
                              <th className="text-left p-2 px-3 font-medium">Extra $/Pax</th>
                              <th className="text-right p-2 px-3 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {zPrices.map((p: any) => (
                              <tr key={p.id} className="border-b last:border-0">
                                <td className="p-2 px-3">
                                  {airports.find((a: any) => a.id === p.airport_id)?.code ?? "-"}
                                </td>
                                <td className="p-2 px-3">
                                  {services.find((s: any) => s.id === p.service_type_id)?.name ?? "-"}
                                </td>
                                <td className="p-2 px-3">${Number(p.base_price).toFixed(2)}</td>
                                <td className="p-2 px-3">{p.max_pax}</td>
                                <td className="p-2 px-3">${Number(p.extra_per_pax).toFixed(2)}</td>
                                <td className="p-2 px-3 text-right">
                                  <Button variant="ghost" size="icon" onClick={() => openEditPrice(p)}>
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => confirm("Delete this price?") && delPrice.mutate(p.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Zone dialog */}
      <Dialog open={zoneOpen} onOpenChange={setZoneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{zoneEditing ? "Edit Zone" : "New Zone"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Code *</Label>
              <Input
                value={zoneForm.code}
                onChange={(e) => setZoneForm({ ...zoneForm, code: e.target.value.toUpperCase() })}
                placeholder="BAVARO"
                className="font-mono"
              />
            </div>
            <div>
              <Label>Name *</Label>
              <Input
                value={zoneForm.name}
                onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                placeholder="Bávaro"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={!!zoneForm.is_active}
                onCheckedChange={(v) => setZoneForm({ ...zoneForm, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZoneOpen(false)}>Cancel</Button>
            <Button onClick={submitZone} disabled={saveZone.isPending}>
              {saveZone.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price dialog */}
      <Dialog open={priceOpen} onOpenChange={setPriceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{priceEditing ? "Edit Price" : "New Price"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Zone</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={priceForm.zone_id}
                onChange={(e) => setPriceForm({ ...priceForm, zone_id: e.target.value })}
                disabled={!!priceEditing}
              >
                <option value="">-- Select --</option>
                {zones.map((z: any) => (
                  <option key={z.id} value={z.id}>{z.code} – {z.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Airport *</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={priceForm.airport_id}
                onChange={(e) => setPriceForm({ ...priceForm, airport_id: e.target.value })}
              >
                <option value="">-- Select --</option>
                {airports.map((a: any) => (
                  <option key={a.id} value={a.id}>{a.code} – {a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Service *</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={priceForm.service_type_id}
                onChange={(e) => setPriceForm({ ...priceForm, service_type_id: e.target.value })}
              >
                <option value="">-- Select --</option>
                {services.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Base Price ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={priceForm.base_price}
                onChange={(e) => setPriceForm({ ...priceForm, base_price: e.target.value })}
              />
            </div>
            <div>
              <Label>Max Pax *</Label>
              <Input
                type="number"
                value={priceForm.max_pax}
                onChange={(e) => setPriceForm({ ...priceForm, max_pax: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Extra $/Pax</Label>
              <Input
                type="number"
                step="0.01"
                value={priceForm.extra_per_pax}
                onChange={(e) => setPriceForm({ ...priceForm, extra_per_pax: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Charged for each passenger above Max Pax.
              </p>
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch
                checked={!!priceForm.is_active}
                onCheckedChange={(v) => setPriceForm({ ...priceForm, is_active: v })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceOpen(false)}>Cancel</Button>
            <Button onClick={submitPrice} disabled={savePrice.isPending}>
              {savePrice.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
